/**
 * Draggable vertical divider between the Document Pane and the Guideline
 * Evaluation section of the Medical Necessity screen.
 *
 * Mirrors the macOS window-split handle: drag to resize, arrow keys to nudge
 * (Shift for larger steps), double-click to reset to the default split.
 * The divider reports the left panel's width as a percentage of the shared
 * container; the parent owns the state so the split resets on each visit.
 * @module PanelDivider
 */
import React, { useCallback, useRef } from "react";

export interface PanelDividerProps {
  /** Left panel width as a percentage of the container (0-100). */
  value: number;
  /** Minimum allowed left-panel percentage. */
  min: number;
  /** Maximum allowed left-panel percentage. */
  max: number;
  /** Called with the new left-panel percentage while dragging or on key press. */
  onChange: (value: number) => void;
  /** Called on double-click to restore the default split. */
  onReset: () => void;
  /** Ref to the flex container that holds both panels and this divider. */
  containerRef: React.RefObject<HTMLDivElement>;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/**
 * Renders the resizable boundary between the two analysis panels.
 *
 * @param props - Current split, bounds, change/reset callbacks, container ref
 * @returns The divider element
 */
export const PanelDivider: React.FC<PanelDividerProps> = ({
  value,
  min,
  max,
  onChange,
  onReset,
  containerRef,
}) => {
  const draggingRef = useRef(false);

  const pctFromClientX = useCallback(
    (clientX: number) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return value;
      return clamp(((clientX - rect.left) / rect.width) * 100, min, max);
    },
    [containerRef, value, min, max],
  );

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    draggingRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    // Prevent text selection in the panels from fighting the drag.
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    onChange(pctFromClientX(event.clientX));
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    event.currentTarget.releasePointerCapture(event.pointerId);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 5 : 1;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      onChange(clamp(value - step, min, max));
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      onChange(clamp(value + step, min, max));
    }
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={Math.round(value)}
      aria-label="Resize document pane"
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onKeyDown={handleKeyDown}
      onDoubleClick={onReset}
      className="group relative z-10 w-[5px] shrink-0 cursor-col-resize bg-primaryGray-15 outline-none transition-colors hover:bg-primaryGray-10 focus-visible:bg-primaryGray-10"
    >
      {/* Wider invisible hit area so the handle is easy to grab. */}
      <div className="absolute inset-y-0 -left-1.5 -right-1.5" />
    </div>
  );
};

export default PanelDivider;
